import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import _findIndex from "lodash/findIndex";
import _isEqual from "lodash/isEqual";
import { batch } from "react-redux";
import { setGlobalGroupBy, setHasCheckedBins } from "store/features/groupBy/groupBySlice";
import { AppThunk } from "store/store";
import { useFilterLandingBinStore } from "stores/filterLandingBinStore";
import { useMapStore } from "stores/map";
import { useMapViewFollowUpStore } from "stores/mapViewFollowUpStore";
import { useWellListOpenStore } from "stores/wellListOpenStore";
import { uid } from "utils";

import {
  generateFilterQuery,
  getFilterCountsFromFilterId,
  getFilterMetadata,
  getWellsFromFilterIdPaginated
} from "api/filter";
import { MapExtent } from "api/useTownshipRangeGrid";

import { LegendItemModel } from "models/LegendItem";
import { EntityKind } from "models/entityKind";
import {
  FilterCountT,
  FilterModel,
  IFilterState,
  IPropertyLegendFilter,
  PolygonFilter,
  PropertyFilterModel,
  SavedFilterModel,
  WellListFilterModel
} from "models/filter";

import toLegendItem from "components/filter/toLegendItem";
import { hasPredicateOn } from "components/filter/util/checkedBins";
import {
  combineFiltersWithPriority,
  getLegendFilter,
  getOthersRollupFilter
} from "components/filter/util";

import { EMPTY_FILTER_ID } from "../../../constants/app.constants";
import { setIsMidstreamNavigatorFilter as setFacilityMidstreamNavigatorFilter } from "./facilityFilterSlice";
import hasOnlyMapPolygonFilterChanges from "./utils/hasOnlyMapPolygonFilterChanges";
import { hasWellListFilter } from "./utils/hasWellListFilter";
import { polygonCoordsEqual } from "./utils/polygonCoordsEqual";

export interface FilterState {
  filterId: {
    id: string;
    lastUpdated: number;
  };
  sortBy: SortByT;
  showGroupsNotInFilter: boolean;
  filterSource: "extent" | "well-list";
  wellListFilter: WellListFilterModel[];
  resetPolygonFilter: boolean;
  updatePlayLegend: string;
  polygonFilter: PolygonFilter;
  excludePolygonsFilter: PolygonFilter[];
  currentWellList: string[]; // well uniqueIds used to check the next filters have the same wells
  propertiesFilter: PropertyFilterModel[];
  wellListFilterExtent?: MapExtent | undefined;
  filterCounts: FilterCountT;
  currentFilter: FilterModel;
  isFiltering: boolean;
  selectedSavedFilter: Maybe<SavedFilterModel>;
  filterHistoryJSON: string[];
  savedFilters: SavedFilterModel[];
  filtersLoaded: boolean;
  currentPlayList: string[];
  isMidstreamNavigatorFilter: boolean;
  // These two stay in this slice, rather than moving to a store of their own,
  // because every reducer that changes the filter has to change them in the
  // same dispatch — a run that read one of them beside a filter the other had
  // already moved on from would filter against a state that never existed.
  //
  // One-shot: leave the map view out of the next filter run, so a well list
  // that was just applied or restored filters on the whole list.
  skipMapViewOnNextFilter: boolean;
  // Sticky: the user removed the map-view node, so the viewport stops
  // narrowing the result until the next well list is applied.
  mapViewFilterDisabled: boolean;
}

export type SortByT =
  | "AlphabeticalAsc"
  | "AlphabeticalDesc"
  | "CountAsc"
  | "CountDesc"
  | "ProdAsc"
  | "ProdDesc";

const initialState: FilterState = {
  filterId: {
    id: "",
    lastUpdated: 0
  },
  sortBy: "AlphabeticalAsc",
  showGroupsNotInFilter: true,
  filterSource: "extent",
  wellListFilter: [],
  polygonFilter: null,
  excludePolygonsFilter: [],
  isFiltering: false,
  wellListFilterExtent: undefined,
  resetPolygonFilter: false,
  updatePlayLegend: "",
  currentWellList: [],
  filterCounts: {},
  propertiesFilter: [],
  currentFilter: undefined, //global currently applied filter
  selectedSavedFilter: { name: "Loading...", filterJSON: "", id: "", filterId: "" },
  filterHistoryJSON: [],
  savedFilters: [],
  filtersLoaded: false,
  currentPlayList: [],
  isMidstreamNavigatorFilter: false,
  skipMapViewOnNextFilter: false,
  mapViewFilterDisabled: false
};

const filterSlice = createSlice({
  name: "filter",
  initialState,
  reducers: {
    updateCurrentFilter(state, action: PayloadAction<FilterModel>) {
      if (action.payload && !action.payload.query.predicates.includes(null)) {
        state.currentFilter = action.payload;
      }
    },
    setFilterCounts(state, action) {
      state.filterCounts = action.payload;
    },
    setFilterState(state, action: PayloadAction<IFilterState>) {
      const filters = action.payload;
      if (!filters) return;
      state.wellListFilter =
        action.payload.wellListFilter && !action.payload.wellListFilter.includes(null)
          ? action.payload.wellListFilter
          : [];
      if (!action.payload.polygonFilter) {
        //no polygon filter saved (i.e. no selection)
        state.resetPolygonFilter = true; // reset filter
        if (state.wellListFilter.length > 0) {
          // A stored well list with no viewport of its own — a saved filter
          // restore. It has to come back whole, so the run that follows leaves
          // the map view out. A payload carrying its own polygon (session
          // snapshot, undo) is reproducing a view and keeps it.
          state.skipMapViewOnNextFilter = true;
        }
      } else {
        state.polygonFilter = action.payload.polygonFilter;
      }

      // Any wholesale replacement of the filter re-enables the map view.
      state.mapViewFilterDisabled = false;
      state.excludePolygonsFilter = action.payload.excludePolygonsFilter ?? [];
      state.propertiesFilter = action.payload.propertiesFilter;
      state.isMidstreamNavigatorFilter = false;
    },
    setFilterId(state, action: PayloadAction<{ id: string; lastUpdated: number }>) {
      const filterId = action.payload;
      // If filterId is null,undefined or empty, don't set filter id
      if (!filterId) return;
      state.filterId = filterId;
      state.filterSource = "well-list";
    },
    setCurrentWellList(state, action) {
      state.currentWellList = action.payload;
    },
    setCurrentPlayList(state, action) {
      state.currentPlayList = action.payload;
    },
    setResetPolygonFilter(state, action) {
      state.resetPolygonFilter = action.payload;
    },
    setUpdatePlayLegend(state, action) {
      state.updatePlayLegend = action.payload;
    },
    setSavedFilters(state, action) {
      state.savedFilters = action.payload;
    },
    removeAllProperties(state) {
      state.propertiesFilter = [];
      state.wellListFilter = [];
      state.excludePolygonsFilter = [];
      state.updatePlayLegend = "";
      state.isMidstreamNavigatorFilter = false;
      // The list this was holding whole is gone, so the viewport is back.
      state.mapViewFilterDisabled = false;
    },
    removeProperty(state, action) {
      const pred = JSON.parse(JSON.stringify(action.payload));
      const properties = JSON.parse(JSON.stringify(state.propertiesFilter));
      const found = properties.filter((p) =>
        p.predicates.find((prop) => {
          return _isEqual(prop, pred);
        })
      );

      if (found.length === 0) {
        return;
      }
      const idx = _findIndex(found[0].predicates, (p) => {
        return _isEqual(p, pred);
      });
      //if predicate is not found then return
      if (idx < 0) {
        return;
      }
      found[0].predicates.splice(idx, 1);
      if (found[0].predicates.length === 0) {
        found.forEach((item) => {
          const index = _findIndex(properties, (p) => _isEqual(p, item));
          if (index > -1) {
            properties.splice(index, 1);
          }
        });
      }
      if (pred.property === "Header.ResourcePlay") {
        state.updatePlayLegend = "";
      }
      state.propertiesFilter = properties;
    },
    filterWellsFromList(
      state,
      action: PayloadAction<{
        wellList: string[];
        resetMidstreamFlag?: boolean;
        source?: string;
      }>
    ) {
      let wellListFilter = null;
      const { wellList, resetMidstreamFlag = true, source } = action.payload;

      wellListFilter = {
        type: "WellList",
        property: "Active",
        operator: "in",
        id: uid(),
        filterType: "well-list",
        value: wellList,
        source
      };

      state.wellListFilter = [wellListFilter];
      state.propertiesFilter = [];
      state.resetPolygonFilter = true; // reset polygon filter
      state.excludePolygonsFilter = [];
      // The list the user just chose is a set of wells, not a statement about
      // the viewport, so it applies whole; the map view starts narrowing again
      // on the run after this one.
      state.skipMapViewOnNextFilter = true;
      state.mapViewFilterDisabled = false;

      // Only reset flag if requested (defaults to true for external operations)
      if (resetMidstreamFlag) {
        state.isMidstreamNavigatorFilter = false;
      }
    },
    setSkipMapViewOnNextFilter(state, action: PayloadAction<boolean>) {
      state.skipMapViewOnNextFilter = action.payload;
    },
    setMapViewFilterDisabled(state, action: PayloadAction<boolean>) {
      state.mapViewFilterDisabled = action.payload;
    },
    setIsFiltering(state, action: PayloadAction<boolean>) {
      state.isFiltering = action.payload;
    },
    ignoreWellsFromWellList(state, wellListPayload) {
      let wellListFilter = null;
      const wellList = wellListPayload.payload;
      if (wellList.length > 0) {
        wellListFilter = {
          type: "WellList",
          property: "Active",
          operator: "nin",
          id: uid(),
          filterType: "well-list",
          value: wellList
        };
        state.wellListFilter.push(wellListFilter);
      }
    },
    removeWellListFilter(state, action) {
      const payload = action.payload;
      const index = state.wellListFilter.findIndex((filter) => filter.id === payload.id);
      if (index < 0) {
        return;
      }
      state.isMidstreamNavigatorFilter = false;
      state.wellListFilter.splice(index, 1);
      // The viewport was only turned off to hold that list whole. With the
      // list gone there is nothing to hold, so the map view comes back.
      state.mapViewFilterDisabled = false;
    },
    filterWellsFromMapExtent(state, polygonPayload) {
      const polygon = polygonPayload.payload;
      // The map view was removed from the filter, so a pan must not put it
      // back — whoever dispatched this, not only the filter watcher.
      if (state.mapViewFilterDisabled && !polygon?.source) return;
      // Skip update if coordinates haven't changed (prevents redundant doFilter on pan)
      if (polygonCoordsEqual(state.polygonFilter, polygon)) return;
      // A polygon the user drew is not a pan: it says which wells to keep, so
      // it lands, and the viewport is on again behind it. Turned on only by an
      // update that lands — a lasso the reducer skipped above changed nothing,
      // and the list is still being held whole.
      state.mapViewFilterDisabled = false;
      //make a copy in case polygon gets cleared
      const json = JSON.stringify(polygon);
      const poly = JSON.parse(json);
      poly.id = uid();
      state.polygonFilter = poly;
    },
    excludeWellsInPolygon(state, polygonPayload) {
      const polygon = polygonPayload.payload;
      //make a copy in case polygon gets cleared
      const json = JSON.stringify(polygon);
      const poly = JSON.parse(json);
      poly.id = uid();
      state.excludePolygonsFilter.push(poly);
    },
    removeExcludePolygon(state, action) {
      const pred = JSON.parse(JSON.stringify(action.payload));
      const polygons = JSON.parse(JSON.stringify(state.excludePolygonsFilter));
      const remaining = polygons.filter((p) => p.id !== pred.id);

      // Nothing removed
      if (remaining.length === polygons.length) {
        return;
      }

      state.excludePolygonsFilter = remaining;
    },
    removeAllExcludePolygon(state) {
      state.excludePolygonsFilter = [];
    },
    setFilterHistory(state, payload: PayloadAction<string[]>) {
      state.filterHistoryJSON = [...payload.payload];
    },
    addToFilterHistory(state, payload: PayloadAction<IFilterState>) {
      if (!hasOnlyMapPolygonFilterChanges(state.filterHistoryJSON, payload.payload)) {
        state.filterHistoryJSON = [
          ...state.filterHistoryJSON,
          JSON.stringify(Object.assign({}, payload.payload))
        ].slice(-200);
      }
    },
    addManyPropertiesToFilter(
      state,
      filterPayload: PayloadAction<IPropertyLegendFilter>
    ) {
      const properties = filterPayload.payload.legendFilters;
      const propertyName = filterPayload.payload.propertyName;
      if (!propertyName) {
        return;
      }
      const props = [...state.propertiesFilter];
      const found = props.filter((p) =>
        p.predicates.find((prop) => prop.property === propertyName)
      );
      found.forEach((item) => {
        const index = props.indexOf(item);
        if (index > -1) {
          props.splice(index, 1);
        }
      });
      if (properties && properties.length > 0) {
        for (const property of properties) {
          for (const pred of property.predicates) {
            pred.id = uid();
          }
          props.push(property);
        }
      }

      state.propertiesFilter = [...props];
      state.isMidstreamNavigatorFilter = false;
    },
    updateSortBy(state, action: PayloadAction<SortByT>) {
      state.sortBy = action.payload;
    },
    updateShowGroupsNotInFilter(state, action: PayloadAction<boolean>) {
      state.showGroupsNotInFilter = action.payload;
    },
    toggleShowGroupsNotInFilter(state) {
      state.showGroupsNotInFilter = !state.showGroupsNotInFilter;
    },
    setSelectedSavedFilter(state, action) {
      state.selectedSavedFilter = action.payload;
    },
    setWellListExtent(state, action: PayloadAction<MapExtent>) {
      state.wellListFilterExtent = action.payload;
    },
    setFiltersLoaded(state, action: PayloadAction<boolean>) {
      state.filtersLoaded = action.payload;
    },
    setIsMidstreamNavigatorFilterReducer(state, action: PayloadAction<boolean>) {
      state.isMidstreamNavigatorFilter = action.payload;
    },
    resetFilterSlice: () => initialState
  }
});

export const setIsMidstreamNavigatorFilter =
  (value: boolean): AppThunk =>
  (dispatch) => {
    // Set the local flag
    dispatch(setIsMidstreamNavigatorFilterReducer(value));

    // If setting to false, also clear the facility filter slice flag to maintain synchronization
    if (!value) {
      dispatch(setFacilityMidstreamNavigatorFilter(false));
    }
  };

export const updateActiveFilterCount =
  (filterId: string): AppThunk =>
  async (dispatch) => {
    if (!filterId || filterId === EMPTY_FILTER_ID) {
      // If filter is null,undefined or empty, don't get filter count
      return;
    }
    const filterCountsResponse = await getFilterCountsFromFilterId(filterId);

    if (filterCountsResponse.status === 200) {
      const filterCounts = filterCountsResponse.data;
      dispatch(setFilterCounts(filterCounts));
    }
  };

export const undoFilter = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const history = [...state.filter.filterHistoryJSON];
  const mapCoordinates = useMapStore.getState().mapExtent.coordinates;

  if (history.length > 1) {
    const item = history[history.length - 2];
    dispatch(setFilterHistory(history.slice(0, -1)));
    const filterState: IFilterState = JSON.parse(item);
    // A history entry can hold no polygon at all: a run that left the map view
    // out writes the filter without one, so stepping back to it has nothing to
    // read a source from and nothing to put today's viewport into.
    const restoresMapView = filterState.polygonFilter?.source !== "selection";
    if (restoresMapView) {
      // reset polygon filter if not being applied
      if (filterState.polygonFilter) {
        filterState.polygonFilter.coordinates = mapCoordinates;
      }
      dispatch(setResetPolygonFilter(true));
    } else {
      // apply polygon lock if reapplying selection filter
      useMapStore.getState().setViewLock(true);
    }
    await dispatch(setFilterState(filterState));
    if (restoresMapView && filterState.wellListFilter?.length > 0) {
      // The polygon coming back is today's viewport, not the one this entry
      // was filtered in. Stepping back to a list has to give the list back
      // whole, the same as applying one does, or an undo hands back fewer
      // wells than it took away.
      dispatch(setSkipMapViewOnNextFilter(true));
    }
  }
};
export const setFilterToSystemDefault = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const wellDefaultFilter = state.filter.savedFilters.find(
    (filter) => filter.name === "System Default"
  );

  if (wellDefaultFilter) {
    try {
      // Parse the filterJSON to get the actual filter data
      const filterData = JSON.parse(wellDefaultFilter.filterJSON);

      // Create filter state with the default properties filter
      const defaultFilterState: IFilterState = {
        wellListFilter: [],
        polygonFilter: null,
        excludePolygonsFilter: [],
        propertiesFilter: filterData.propertiesFilter || []
      };

      await dispatch(setFilterState(defaultFilterState));
    } catch (error) {
      // If JSON parsing fails, set empty filter state
      const emptyFilterState: IFilterState = {
        wellListFilter: [],
        polygonFilter: null,
        excludePolygonsFilter: [],
        propertiesFilter: []
      };

      await dispatch(setFilterState(emptyFilterState));
    }
  }
};
export const addLegendItemToFilter =
  (title: string, groupByProperty?: string | undefined): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const legendItems = useMapStore.getState().txnId?.legend?.legendItems as
      | LegendItemModel[]
      | undefined;
    if (!legendItems) {
      return;
    }
    const items: LegendItemModel[] = legendItems.filter(
      (li: LegendItemModel) => li.groupTitle === title || li.title === title
    );
    if (items.length === 0) {
      return;
    }
    const item = toLegendItem(
      items,
      state.groupBy?.globalGroupBy,
      state.filter.propertiesFilter
    )[0];
    const filter = getLegendFilter(
      item.property,
      item.canBin,
      [item].map((item) => item.title),
      item.categoryId,
      item.bin,
      item.normalizeBySettings,
      item.isNotEqual,
      item.pdenSource,
      item.value,
      item.groupByTitle,
      item.partialContextTitle
    );
    dispatch(
      addManyPropertiesToFilter({
        legendFilters: [filter],
        propertyName: groupByProperty ?? state.groupBy.globalGroupBy?.property
      })
    );
  };

export const addMultipleLegendItemsToFilter =
  (
    titles: string[],
    isExclude: boolean,
    groupByProperty?: string | undefined
  ): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const legendItems = useMapStore.getState().txnId?.legend?.legendItems as
      | LegendItemModel[]
      | undefined;
    const currentFilter = state.filter.propertiesFilter;
    if (!legendItems) {
      return;
    }
    const items: LegendItemModel[] = legendItems
      .filter(
        (li: LegendItemModel) =>
          titles.includes(li.groupTitle) || titles.includes(li.title)
      )
      .map((item: LegendItemModel) => {
        return { ...item, isNotEqual: true };
      });
    if (items.length === 0) {
      return;
    }
    const newLegendItems = toLegendItem(
      items,
      state.groupBy?.globalGroupBy,
      state.filter.propertiesFilter
    );
    // Others can only be excluded, never included.
    const lassoItems = newLegendItems.legendItems.filter(
      (item) => isExclude || !item.isOthersRollup
    );
    if (lassoItems.length === 0) {
      return;
    }
    const legendFilters = lassoItems.map((item) => {
      if (item.isOthersRollup) {
        return getOthersRollupFilter(
          item.property,
          item.categoryId,
          item.bin,
          isExclude,
          item.pdenSource,
          item.groupByTitle,
          item.partialContextTitle,
          item.forecastTrumpingSettings,
          item.performanceDateRange
        );
      }
      return getLegendFilter(
        item.property,
        item.canBin,
        [item].map((item) => item.title),
        item.categoryId,
        item.bin,
        item.normalizeBySettings,
        isExclude,
        item.pdenSource,
        item.value,
        item.groupByTitle,
        item.partialContextTitle
      );
    });
    const combinedFilters = [
      ...combineFiltersWithPriority(currentFilter, legendFilters, legendItems)
    ];
    dispatch(
      addManyPropertiesToFilter({
        legendFilters: isExclude ? combinedFilters : legendFilters,
        propertyName: groupByProperty ?? state.groupBy.globalGroupBy?.property
      })
    );
  };

// Content hash of the last-applied well list, used only to skip redundant
// map-extent updates when reapplying a filter yields the same wells. Thunk-
// local memoization, not app state — doesn't belong in Redux (no new Redux
// state per state-management.instructions.md) and nothing else reads it.
let previousWellListContentHash = "";

// Sets the well filter's new id with the state staged for it before the run started at
// `revision` (filterLandingBinStore), so no widget groups one filter by the other's bins.
// Callers wrap it in batch() for one update.
function landWellFilterId(
  dispatch: Parameters<AppThunk>[0],
  getState: Parameters<AppThunk>[1],
  id: string,
  revision: number
) {
  const landing = useFilterLandingBinStore.getState().land(revision);
  dispatch(setFilterId({ id, lastUpdated: Date.now() }));
  if (!landing || (!landing.bin && !landing.releasesHold)) return;
  const { globalGroupBy } = getState().groupBy;
  // Read off the live filter and focus as it lands, so a focus change in flight is
  // answered too; the checked list stays the saved filter's until the legend rebuilds.
  if (landing.releasesHold) {
    dispatch(
      setHasCheckedBins(
        hasPredicateOn(getState().filter.propertiesFilter, globalGroupBy.property)
      )
    );
  }
  if (!landing.bin) return;
  if (landing.bin.property === globalGroupBy.property) {
    dispatch(setGlobalGroupBy({ ...globalGroupBy, bin: landing.bin.bin }));
  }
}

export const doFilter =
  (getFilter, zoomToFilterEnabled = false, isUserFilterChange = true): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const checkedPadScenarios = state.app.checkedPadScenarios;
    dispatch(setIsFiltering(true));
    // In-memory only: a filter id that outlived its page load may belong to another
    // tab (Chrome duplicates/restores clone web storage), and naming it "previous"
    // would expire a filter still in use (EVA-69). EMPTY_FILTER_ID is a truthy
    // sentinel for "nothing applied", never an id the backend can clean up.
    const currentFilterId = state.filter.filterId.id;
    const previousFilterId = currentFilterId === EMPTY_FILTER_ID ? "" : currentFilterId;
    const polygonFilter = state.filter.polygonFilter;
    const hasWellList = hasWellListFilter(state.filter?.wellListFilter);
    const openToken = useWellListOpenStore
      .getState()
      .take(state.filter.wellListFilter?.[0]?.id);
    // Removing the map-view node only means anything while a well list is
    // carrying the result. With no list the viewport is the whole filter, so
    // it keeps working the way it always has.
    const mapViewFilterDisabled = state.filter.mapViewFilterDisabled && hasWellList;
    // The map view is the one polygon nobody marks. A polygon that says where
    // it came from is the user's own, and leaving it out would quietly widen
    // the result instead of holding a list whole.
    const isMapViewPolygon = !polygonFilter?.source;
    // No viewport yet means no map to filter against — unless the map view was
    // removed from the filter, in which case there is nothing to wait for.
    if (!polygonFilter?.type?.length && !mapViewFilterDisabled) {
      dispatch(setIsFiltering(false));
      return;
    }
    // Read after the guard above, never beside getState(): a run that bailed
    // out issued no request, so spending the flag there would narrow the first
    // real run after the map mounts.
    const skipMapView = state.filter.skipMapViewOnNextFilter;
    // Leaving the map view out is only ever about holding a list whole. With
    // no list there is nothing to hold, and dropping the viewport would filter
    // against nothing at all.
    const omitMapView =
      hasWellList && isMapViewPolygon && (skipMapView || mapViewFilterDisabled);
    // Spent only by the run that used it, and only once that run has landed.
    // A newer run reads this state and builds its query before it cancels the
    // one in flight, so spending the flag up front would hand the replacement
    // a query with the viewport in it and narrow the list this flag was
    // holding whole. A run that kept the map view anyway — a lasso is on file,
    // or there is no list — never took its turn either.
    const skipMapViewUsed = skipMapView && omitMapView;
    // A run held the map view out for one turn owes it a turn of its own: the
    // two runs read the same state apart from the flag, so nothing else can
    // tell that the viewport never got its say.
    const owesMapViewFollowUp = omitMapView && !mapViewFilterDisabled;
    // What was staged for a filter before this run read it is this run's to land.
    const landingRevision = useFilterLandingBinStore.getState().revision;
    const query = generateFilterQuery(
      state.filter.wellListFilter,
      omitMapView ? null : state.filter.polygonFilter,
      state.filter.excludePolygonsFilter,
      state.filter.propertiesFilter,
      previousFilterId,
      null,
      EntityKind.Well,
      // This is the live filter, so an unmarked polygon here really is the map
      // the user is looking at.
      true
    );
    if (checkedPadScenarios.length > 0) {
      query.scenarioIds = checkedPadScenarios.map((s) => s.scenarioId);
    }
    const currentFilterState: IFilterState = {
      polygonFilter: state.filter.polygonFilter,
      excludePolygonsFilter: state.filter.excludePolygonsFilter,
      wellListFilter: state.filter.wellListFilter,
      propertiesFilter: state.filter.propertiesFilter
    };
    dispatch(addToFilterHistory(currentFilterState));
    const result = await getFilter(query);
    let filterId = "";
    if (result && result.ok) {
      filterId = result.value;
    } else {
      // Clear the flag on the way out, the same as every other bail-out in this
      // thunk. Leaving it set marks a filter request as permanently in flight,
      // which anything reading it then waits on for the rest of the session.
      //
      // A cancelled request is the exception: it was superseded by a newer
      // filter that is still running, and clearing the flag on its behalf would
      // announce that request finished too. Same guard the well map updater
      // applies to its own cancellations.
      if (result?.error !== "cancelled") {
        dispatch(setIsFiltering(false));
        useFilterLandingBinStore.getState().settle(landingRevision);
      }
      return;
    }
    let metadataResponse: Awaited<ReturnType<typeof getFilterMetadata>>;
    try {
      metadataResponse = await getFilterMetadata(filterId);
      if (metadataResponse.status === 200 && metadataResponse.data) {
        dispatch(setCurrentPlayList(metadataResponse.data.resourcePlays));
      } else {
        throw new Error(`getFilterMetadata returned ${metadataResponse.status}`);
      }
    } catch (err) {
      dispatch(setIsFiltering(false));
      useFilterLandingBinStore.getState().settle(landingRevision);
      return;
    }

    // The list has been filtered whole, so the one-shot is spent.
    if (skipMapViewUsed) {
      dispatch(setSkipMapViewOnNextFilter(false));
    }
    batch(() => {
      dispatch(updateCurrentFilter(query));
      landWellFilterId(dispatch, getState, filterId, landingRevision);
    });
    if (hasWellList || zoomToFilterEnabled) {
      const newHash = metadataResponse.data.contentHash;
      // Two ways to earn a new zoom extent, and no others.
      //
      // The first is the same result set with no extent to show it in. This
      // hash is a module-level `let` that survives resetFilterSlice() (logout
      // is an SPA route change, no reload), so after logout the Redux extent
      // is undefined while the hash still holds the old value — the extent has
      // to be restored even though nothing about the result changed.
      //
      // The second is a result set the user asked for and actually got: a
      // different hash, from a filter change of their own. A refilter the
      // viewport itself caused must not move the viewport, or panning refits
      // the camera, which pans again, and the result keeps shrinking. That
      // also keeps the extent on the run carrying the whole list rather than
      // the narrowed run that follows it.
      const extentUnset = state.filter.wellListFilterExtent === undefined;
      const hashChanged = previousWellListContentHash !== newHash;
      // Recorded by every run that came back with a result, not only by the
      // ones that earned an extent. A run that skipped the extent still saw
      // this result set, so a later run returning the same one has changed
      // nothing and must not read as a change.
      previousWellListContentHash = newHash;
      const wellListOpen = useWellListOpenStore.getState();
      if (isUserFilterChange) {
        // A newer user filter supersedes an open still in flight.
        wellListOpen.setPending(undefined);
        if (openToken != null && metadataResponse.data.totalCount === 1) {
          const uwi = await getWellsFromFilterIdPaginated(filterId, 0, 1)
            .then((r) => r?.data?.uwiList?.[0]?.uniqueId)
            .catch(() => undefined);
          // A newer run landed while this one waited and now owns the extent
          // and the filtering flag, as with a cancelled request.
          if (getState().filter.filterId.id !== filterId) return;
          if (uwi) {
            // Set before the extent, so the auto-zoom sees it and leaves the
            // one camera move to the map's single-well open.
            wellListOpen.setPending({
              uwi,
              extent: metadataResponse.data.boundingBox,
              at: Date.now(),
              token: openToken
            });
          }
        }
      }
      if ((extentUnset && !hashChanged) || (isUserFilterChange && hashChanged)) {
        dispatch(setWellListExtent(metadataResponse.data.boundingBox));
      }
    } else {
      previousWellListContentHash = "";
      dispatch(setWellListExtent(undefined));
    }
    // A run that came back settles the debt outright: it either left the map
    // view out and owes it a turn, or it carried the map view and nothing is
    // outstanding — including anything an earlier filter left behind.
    useMapViewFollowUpStore
      .getState()
      .setMapViewFollowUpOwed(EntityKind.Well, owesMapViewFollowUp);
    dispatch(setIsFiltering(false));
    return filterId;
  };

// The well list input dispatches this right after its list. Only that list
// may open a one-well result, so a lasso or a restore never does.
export const armWellListOpen =
  (token: number): AppThunk =>
  (_dispatch, getState) => {
    useWellListOpenStore
      .getState()
      .setArm(getState().filter.wellListFilter[0]?.id, token);
  };

// Stores a zoom extent for the already-applied well filter, keeping the
// content hash current so the next doFilter still skips redundant extent
// updates for an unchanged result set. Used by the zoomToFilterResults
// coordinator when the zoom-to-filter toggle is switched on.
export const applyZoomToFilterExtent =
  (extent: MapExtent, contentHash: string): AppThunk =>
  (dispatch) => {
    previousWellListContentHash = contentHash;
    dispatch(setWellListExtent(extent));
  };

export const clearWellFilter = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  if (state.filter.filterId.id === EMPTY_FILTER_ID) return;
  const { revision } = useFilterLandingBinStore.getState();
  batch(() => landWellFilterId(dispatch, getState, EMPTY_FILTER_ID, revision));
};

export const {
  updateCurrentFilter,
  setFilterId,
  setFilterCounts,
  updateSortBy,
  updateShowGroupsNotInFilter,
  toggleShowGroupsNotInFilter,
  setFilterState,
  setCurrentWellList,
  setCurrentPlayList,
  setFilterHistory,
  setResetPolygonFilter,
  setUpdatePlayLegend,
  removeProperty,
  filterWellsFromList,
  setSkipMapViewOnNextFilter,
  setMapViewFilterDisabled,
  removeAllProperties,

  removeWellListFilter,
  setWellListExtent,
  filterWellsFromMapExtent,
  excludeWellsInPolygon,
  setIsFiltering,
  removeExcludePolygon,
  removeAllExcludePolygon,
  addManyPropertiesToFilter,
  setSelectedSavedFilter,
  setSavedFilters,
  addToFilterHistory,
  setFiltersLoaded,
  setIsMidstreamNavigatorFilterReducer,
  resetFilterSlice
} = filterSlice.actions;

export default filterSlice.reducer;
